import { apiClient } from "@/api/api-client";
import { ApiResponse, PageContent } from "@/types/api-response";
import { endpoints } from "./endpoints/endpoints";

// Daily stock interface based on the API response
export interface DailyStock {
  id: string;
  storeId: string;
  productId: string;
  variantId: string;
  stockDate: string;
  openQty: number | null;
  stockInHand: number | null;
  soldQty: number;
  receiveQty: number;
  adjustInQty: number;
  adjustOutQty: number;
  closingQty: number;
  locked: boolean | null;
}

// Extended stock interface with variant information (flattened from SQL join)
export interface StockWithVariant extends DailyStock {
  variantName: string;
  sku: string;
  imageUrl: string | null;
  price: number | null;
  isActive: boolean | null;
}

export interface DailyStockSummary {
  totalItems: number;
  outOfStockItems: number;
  lowStockItems: number;
}

// Get today's daily stock
export async function getTodayDailyStock(params?: {
  page?: number;
  size?: number;
  search?: string;
  status?: string;
}): Promise<ApiResponse<PageContent<StockWithVariant>>> {
  const response = await apiClient.get<PageContent<StockWithVariant>, object>(
    "/daily-stocks",
    params
  );
  return response;
}

// Get daily stock summary
export async function getDailyStockSummary(): Promise<ApiResponse<DailyStockSummary>> {
  const response = await apiClient.get<DailyStockSummary, object>(
    "/daily-stocks/summary"
  );
  return response;
}

// Get daily stock by date range
export async function getDailyStockByDateRange(
  startDate: string,
  endDate: string,
  params?: {
    storeId?: number;
    productId?: string;
    variantId?: string;
  }
): Promise<ApiResponse<DailyStock[]>> {
  const queryParams = {
    startDate,
    endDate,
    ...params,
  };
  const response = await apiClient.get<DailyStock[], object>(
    "/daily-stocks/date-range",
    queryParams
  );
  return response;
}

// Get daily stock with product information
export async function getDailyStockWithProducts(): Promise<
  ApiResponse<StockWithVariant[]>
> {
  const response = await apiClient.get<StockWithVariant[], object>(
    "/daily-stocks/with-products"
  );
  return response;
}

// Update stock quantity
export async function updateStockQuantity(
  stockId: number,
  data: {
    adjustInQty?: number;
    adjustOutQty?: number;
    closingQty?: number;
    notes?: string;
  }
): Promise<ApiResponse<DailyStock>> {
  const response = await apiClient.patch<DailyStock, typeof data>(
    `/daily-stocks/${stockId}`,
    data
  );
  return response;
}

// Bulk update stock quantities
export async function bulkUpdateStockQuantities(
  updates: Array<{
    stockId: number;
    adjustInQty?: number;
    adjustOutQty?: number;
    closingQty?: number;
    notes?: string;
  }>
): Promise<ApiResponse<DailyStock[]>> {
  const response = await apiClient.post<DailyStock[], typeof updates>(
    "/daily-stocks/bulk-update",
    updates
  );
  return response;
}

// Stock movement types
export type StockMovementType =
  | "sale"
  | "receipt"
  | "adjustment"
  | "transfer"
  | "return";

// Create stock movement
export async function createStockMovement(data: {
  productId: string;
  variantId: string;
  storeId: number;
  movementType: StockMovementType;
  quantity: number;
  notes?: string;
  reference?: string;
}): Promise<ApiResponse<{ message: string; stockId: number }>> {
  const response = await apiClient.post<
    { message: string; stockId: number },
    typeof data
  >("/stock-movements", data);
  return response;
}

export interface LastClosingDateResponse {
  lastClosingDate: string;
  missingStockVariantsCount: number;
}

export async function getLastClosingDate() {
  const response = await apiClient.get<LastClosingDateResponse, {}>(
    "/daily-stocks/last-closing"
  );
  return response;
}

export async function recomputeAllStock() {
  const response = await apiClient.post<string, {}>(
    endpoints.stock.recomputeAllStock
  );
  return response;
}

export async function getRemainingQty(variantId: string): Promise<ApiResponse<DailyStock>> {
  const url = `/product-variants/${variantId}`;
  const response = await apiClient.get<DailyStock, {}>(
    url
  );
  return response;
}
